import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import apiUrl from "../../Config";

import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Context from "../../context/Context";
import { find } from "lodash";
import { toast } from "react-toastify";
import DeleteConfirm from "../../Common/DeleteConfirm";
import { Plus } from "lucide-react";

const DisplayTimeslot = () => {
  const {
    isConfirmDeleteModelOpen,
    setIsConfirmDeleteModelOpen,
    itemIdToDelete,
    confirmDelete,
  } = useContext(Context);
  const [timeslot, setTimeslot] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getAllTimeSlots = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/slots/getalltimeslots`);
      if (res?.data?.status === true) {
        setTimeslot(res.data.slots);
      }
    } catch (error) {
      setError("Error fetching time slots:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (slot) => {
    navigate("/create-edit-timeslots", { state: { slot } });
  };

  const handleDelete = async () => {
    setIsConfirmDeleteModelOpen(false);
    setLoading(true);

    try {
      const res = await axios.delete(
        `${apiUrl}/slots/deleteslot/${itemIdToDelete}`
      );
      if (res?.data?.status === true) {
        toast.success(res?.data?.success || " Time slot deleted successfully");
        getAllTimeSlots();
      }
    } catch (error) {
      setError("Error deleting time slot:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate("/create-edit-timeslots");
  };

  useEffect(() => {
    getAllTimeSlots();
  }, []);

  return (
    <>
      <Box p={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={1.6}
          mb={3}
          flexWrap="wrap"
        >
          <Typography variant="h5" fontWeight="bold" mb={isMobile ? 2 : 0}>
            Time Slots
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreate}
            sx={{
              borderRadius: "8px",
              padding: "8px 20px",
              display: "flex",
              gap: 1,
            }}
          >
            <Plus size={23} />
            <Typography>New Time Slot</Typography>
          </Button>
        </Box>

        {/* Responsive horizontal scroll on small screens */}
        <Divider sx={{ mb: 2 }} />
        <Box>
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Is Peak Hour</TableCell>
                  <TableCell>Peak Hour Amount</TableCell>
                  <TableCell>Is Active</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {timeslot.map((slot, index) => (
                  <TableRow key={index}>
                    <TableCell>{slot.time}</TableCell>
                    <TableCell>{slot.isPeakHour ? "Yes" : "No"}</TableCell>
                    <TableCell>{slot.peakHourAmount}</TableCell>
                    <TableCell>{slot.isActive ? "Yes" : "No"}</TableCell>
                    <TableCell align="center">
                      <IconButton color="" onClick={() => handleEdit(slot)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => confirmDelete(slot.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {timeslot.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No time slots available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </Box>

      <DeleteConfirm
        open={isConfirmDeleteModelOpen}
        onClose={() => setIsConfirmDeleteModelOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default DisplayTimeslot;
